'use client';

import { Fragment, Suspense, useState } from 'react';

import { Menu } from 'lucide-react';
import Link from 'next/link';

import { Button } from '@naumu/ui/components/button';
import { SideNavDrawer } from '@naumu/ui/components/side-nav-drawer';

import { MOBILE_NAV_LINKS } from '../config/marketing-nav';
import { MarketingAuthActions } from './marketing-auth-actions';

/**
 * MarketingMobileNav - the mobile burger + nav drawer for every public page,
 * built on the same SideNavDrawer shell the docs nav uses. Hidden at `md`+,
 * where MarketingNavLinks (the hover-popover nav) takes over.
 *
 * The drawer reads MOBILE_NAV_LINKS: each top-level link with sub-items becomes
 * a labelled section of those sub-links; plain links (Use cases, Pricing) render
 * as standalone rows, spaced like the groups and carrying the same boxed icon
 * when the registry gives them one. Use cases is a designed panel on desktop and
 * a single row here - two entries in the registry, split by `only`. The full auth
 * set (Sign in / Sign out / All spaces) is repeated in the footer for thumb
 * reach. The shell closes on any body tap, so a link tap dismisses the drawer.
 */
export const MarketingMobileNav = () => {
	const [open, setOpen] = useState(false);

	return (
		<>
			<Button
				variant="ghost"
				size="sm"
				iconOnly
				type="button"
				aria-label="Open menu"
				onClick={() => setOpen(true)}
				className="md:hidden"
			>
				<Menu />
			</Button>

			<SideNavDrawer
				open={open}
				onClose={() => setOpen(false)}
				title="Site navigation"
				label="Menu"
				className="dark border-r border-white/10 bg-black/95 backdrop-blur-xl"
			>
				<div className="flex min-h-full flex-col">
					<nav aria-label="Site" className="flex flex-col px-3 py-2">
						{MOBILE_NAV_LINKS.map((link) =>
							link.items?.length ? (
								<div key={link.label} className="mt-5 first:mt-0">
									{/* Popover-only groups (e.g. Resources) have no page of their own -
									    render a plain heading instead of a dead `#` link. */}
									{link.href ? (
										<Link
											href={link.href}
											className="block rounded-lg px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-white/40 transition-colors hover:text-white/70"
										>
											{link.label}
										</Link>
									) : (
										<span className="block px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-white/40">
											{link.label}
										</span>
									)}
									<div className="mt-0.5 flex flex-col">
										{link.items.map((item, index) => {
											const Icon = item.icon;
											const showGroup =
												!!item.group && item.group !== link.items?.[index - 1]?.group;
											return (
												<Fragment key={item.href}>
													{showGroup && (
														<span className="mt-2 block px-3 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/30 first:mt-0">
															{item.group}
														</span>
													)}
													<Link
														href={item.href}
														className="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-white/5"
													>
														{Icon && (
															<span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.08] text-white">
																<Icon className="size-[16px]" />
															</span>
														)}
														<span className="text-[14px] font-medium text-white/90">
															{item.label}
														</span>
													</Link>
												</Fragment>
											);
										})}
									</div>
								</div>
							) : (
								<Link
									key={link.label}
									href={link.href ?? '#'}
									className="mt-5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-white/80 transition-colors first:mt-0 hover:bg-white/5"
								>
									{link.icon && (
										<span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.08] text-white">
											<link.icon className="size-[16px]" />
										</span>
									)}
									{link.label}
								</Link>
							),
						)}
					</nav>

					<div className="mt-auto grid gap-2 border-t border-white/10 px-4 py-4">
						{open && (
							<Suspense
								fallback={<div className="h-11 w-full animate-pulse rounded-[12px] bg-white/5" />}
							>
								<MarketingAuthActions layout="drawer" />
							</Suspense>
						)}
					</div>
				</div>
			</SideNavDrawer>
		</>
	);
};
